import React, { useEffect, useMemo, useRef } from "react";

const sanitize = (value) => (typeof value === "string" ? value.trim() : "");

const MustVisitSection = ({ fields, sectionId }) => {
    const desktopTrackRef = useRef(null);
    const mobileTrackRef = useRef(null);

    // SAFE dynamic values
    const eyebrow = sanitize(fields?.eyebrow);
    const title = sanitize(fields?.title);

    // Extract image URLs and alt text safely
    const images = Array.isArray(fields?.images)
        ? fields.images.map((item) => ({
            url: item?.image?.url || "",
            alt: item?.image?.alt || ""
        })).filter(item => item.url)
        : [];

    // Desktop REPEATED IMAGE LIST — required for seamless infinite scroll
    const desktopTrack = useMemo(() => {
        if (!images.length) return [];
        return [...images, ...images, ...images]; // repeat 3× for smooth scroll
    }, [images]);

    // Mobile track repetition
    const mobileTrack = useMemo(() => {
        if (!images.length) return [];
        return [...images, ...images, ...images];
    }, [images]);

    const mobileTrackReversed = useMemo(() => {
        if (!images.length) return [];
        const reversed = [...images].reverse();
        return [...reversed, ...reversed, ...reversed];
    }, [images]);

    const shouldRender = Boolean(eyebrow || title || images.length);

    /* =====================================================
       Pixel-based Animation Setup (after images loaded)
    ===================================================== */
    useEffect(() => {
        if (!images.length) return;

        const speed = 20; // pixels per second

        const checkAndSetup = () => {
            // Use requestAnimationFrame to batch layout reads and prevent forced reflow
            requestAnimationFrame(() => {
                // Desktop setup
                if (desktopTrackRef.current) {
                    const track = desktopTrackRef.current;
                    const logosContainer = track.closest('.mv-logos');
                    const set1 = track.querySelector('.set_1_d');

                    if (set1 && logosContainer) {
                        // Batch read: get layout property once
                        const setWidth = Math.round(set1.getBoundingClientRect().width);
                        if (setWidth > 0) {
                            // Batch write: apply all styles together
                            logosContainer.style.setProperty('--set-width-px-d', setWidth + 'px');
                            logosContainer.style.setProperty('--anim-duration-d', (setWidth / speed) + 's');
                            logosContainer.classList.add('mv-ready');
                        }
                    }
                }

                // Mobile setup
                if (mobileTrackRef.current) {
                    const track = mobileTrackRef.current;
                    const logosContainer = track.closest('.mv-logos-m');
                    const set1 = track.querySelector('.set_1');

                    if (set1 && logosContainer) {
                        // Batch read: get layout property once
                        const setWidth = Math.round(set1.getBoundingClientRect().width);
                        if (setWidth > 0) {
                            // Batch write: apply all styles together
                            logosContainer.style.setProperty('--set-width-px-m', setWidth + 'px');
                            logosContainer.style.setProperty('--anim-duration-m', (setWidth / speed) + 's');
                            logosContainer.classList.add('mv-ready');
                        }
                    }
                }
            });
        };

        // Wait for images to load
        const setupAfterImagesLoad = () => {
            const allImages = [
                ...(desktopTrackRef.current?.querySelectorAll('img') || []),
                ...(mobileTrackRef.current?.querySelectorAll('img') || [])
            ];

            if (allImages.length === 0) {
                setTimeout(checkAndSetup, 500);
                return;
            }

            let loadedCount = 0;
            const totalImages = allImages.length;

            const handleImageLoad = () => {
                loadedCount++;
                if (loadedCount >= totalImages) {
                    // Small delay to ensure layout is calculated
                    setTimeout(checkAndSetup, 100);
                }
            };

            allImages.forEach(img => {
                if (img.complete) {
                    handleImageLoad();
                } else {
                    img.addEventListener('load', handleImageLoad, { once: true });
                    img.addEventListener('error', handleImageLoad, { once: true });
                }
            });
        };

        // Start checking after DOM is ready
        const timer = setTimeout(setupAfterImagesLoad, 100);
        return () => clearTimeout(timer);
    }, [images, desktopTrack.length, mobileTrack.length, mobileTrackReversed.length]);

    /* =====================================================
       Scroll Animation (Lookout)
    ===================================================== */
    useEffect(() => {
        const section = document.querySelector(".section-lookout");
        const card = section?.querySelector(".lookout-card");

        const onScroll = () => {
            if (!section || !card) return;

            const secRect = section.getBoundingClientRect();
            const cardRect = card.getBoundingClientRect();
            const viewH = window.innerHeight;

            const inView = secRect.top < viewH * 0.7 && secRect.bottom > viewH * 0.25;
            const covered = cardRect.top < 160;

            if (inView && !covered) {
                section.classList.add("active");
                section.classList.remove("covered");
            } else if (inView && covered) {
                section.classList.add("covered");
                section.classList.remove("active");
            } else {
                section.classList.remove("active", "covered");
            }
        };

        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    /* Accessibility: only one heading exposed to screen readers (matches 768px breakpoint) */
    useEffect(() => {
        if (!title) return;
        const mq = window.matchMedia("(max-width: 768px)");
        const update = () => {
            const desktopHeading = document.querySelector(".must-visit .mv-title");
            const mobileHeading = document.querySelector(".must-visit-mobile .mv-title-m");
            if (!desktopHeading || !mobileHeading) return;
            if (mq.matches) {
                desktopHeading.setAttribute("aria-hidden", "true");
                mobileHeading.removeAttribute("aria-hidden");
            } else {
                desktopHeading.removeAttribute("aria-hidden");
                mobileHeading.setAttribute("aria-hidden", "true");
            }
        };
        update();
        mq.addEventListener("change", update);
        return () => mq.removeEventListener("change", update);
    }, [title, images.length]);

    // Desktop now uses CSS animation (no Slick slider) for seamless infinite scroll

    if (!shouldRender) return null;

    /* =====================================================
       RENDER SECTION
    ===================================================== */
    return (
        <>
            {/* ===== Desktop Version ===== */}
            <section className="must-visit">
                <div className="mv-wrap">
                    {(eyebrow || title) && (
                        <div className="mv-head" data-stagger-index="0">
                            {eyebrow && <span className="mv-eyebrow">{eyebrow}</span>}
                            {title && <h2 className="mv-title">{title}</h2>}
                        </div>
                    )}

                    {desktopTrack.length > 0 && (
                        <div className="mv-logos" data-section-id={sectionId}>
                            <div className="mv-track-d" ref={desktopTrackRef}>
                                <div className="set_1_d">
                                    {desktopTrack.map((img, index) => (
                                        <div className="mv-logo" key={`d1-${index}`}>
                                            <img src={img.url} alt={img.alt || `View ${index + 1}`} />
                                        </div>
                                    ))}
                                </div>
                                <div className="set_2_d">
                                    {desktopTrack.map((img, index) => (
                                        <div className="mv-logo" key={`d2-${index}`}>
                                            <img src={img.url} alt={img.alt || `View ${index + 1}`} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* ===== Mobile Version ===== */}
            {images.length > 0 && (
                <section className="must-visit-mobile">
                    <div className="mv-wrap-m">
                        {(eyebrow || title) && (
                            <div className="mv-head-m" data-stagger-index="0">
                                {eyebrow && <span className="mv-eyebrow-m">{eyebrow}</span>}
                                {title && <h2 className="mv-title-m">{title}</h2>}
                            </div>
                        )}

                        <div className="mv-logos-m">
                            <div className="mv-track-m" ref={mobileTrackRef}>
                                <div className="set_1">
                                    {mobileTrack.map((img, index) => (
                                        <div className="mv-logo-m" key={`s1-${index}`}>
                                            <img src={img.url} alt={img.alt || `View ${index + 1}`} />
                                        </div>
                                    ))}
                                </div>

                                <div className="set_2">
                                    {mobileTrackReversed.map((img, index) => (
                                        <div className="mv-logo-m" key={`s2-${index}`}>
                                            <img src={img.url} alt={img.alt || `View ${index + 1}`} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}
        </>
    );
};

export default MustVisitSection;
